import React, { useEffect, useState } from 'react';
import { Loader2, Github } from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';
import {
  fetchSiteGithub, fetchGithubAppManifest, startGithubConnect, fetchGithubRepos, fetchGithubBranches, saveSiteGithub, deployFromGithub, setGithubAutoPublish, disconnectGithub,
  type SiteGithub, type GithubRepo, type Site,
} from '@/services/siteApi';

// Settings: deploy the site from a GitHub repo (siteGithub.js on the server). Every
// push to the picked branch becomes a version on the preview address, or goes live
// when "Publish every push" is on.
const INK = 'var(--hj-ink)';
const MUTED = 'var(--hj-ink-2)';
const panel: React.CSSProperties = { background: '#fff', border: '1px solid var(--hj-hairline-soft)', borderRadius: 16, padding: 24 };
const h2: React.CSSProperties = { margin: 0, fontFamily: 'var(--hj-font-display)', fontWeight: 800, fontSize: 18, letterSpacing: '-0.01em', color: INK };
const small: React.CSSProperties = { fontSize: 14, color: MUTED, margin: 0, lineHeight: 1.45 };
const errText: React.CSSProperties = { ...small, color: 'var(--hj-pink-ink)' };
const label: React.CSSProperties = { fontWeight: 600, fontSize: 14, color: INK };
const input: React.CSSProperties = { width: '100%', boxSizing: 'border-box', minHeight: 44, border: '1px solid var(--hj-hairline)', borderRadius: 10, padding: '10px 12px', font: '15px var(--hj-font-ui)', color: INK, background: '#fff' };
const dark: React.CSSProperties = { display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8, minHeight: 44, padding: '10px 16px', fontFamily: 'inherit', fontSize: 15, fontWeight: 600, background: INK, color: '#fff', border: 0, borderRadius: 10, cursor: 'pointer' };
const linkBtn: React.CSSProperties = { background: 'none', border: 0, padding: 0, fontFamily: 'inherit', fontSize: 14, fontWeight: 600, color: INK, textDecoration: 'underline', cursor: 'pointer', minHeight: 32 };
const when = (s: string) => new Date(s).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });

export const SiteGithubPanel: React.FC<{ site: Site; onSite: (s: Site) => void }> = ({ site, onSite }) => {
  const [g, setG] = useState<SiteGithub | null>(null);
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);

  const load = () => fetchSiteGithub(site.id).then((r) => setG(r.github)).catch(() => setG(null));
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [site.id]);
  // A push deploys in the background; check back while one is running.
  useEffect(() => {
    if (!g?.deploying) return;
    const t = setInterval(() => fetchSiteGithub(site.id).then((r) => setG(r.github)).catch(() => {}), 5000);
    return () => clearInterval(t);
  }, [g?.deploying, site.id]);
  useEffect(() => {
    const q = new URLSearchParams(window.location.search).get('github');
    if (q === 'connected') setEditing(true);
    if (q === 'none') setErr('GitHub didn’t give Hey Joe Sites access to any repos. Connect again and pick the repo with your site.');
    if (q === 'failed') setErr('Connecting to GitHub didn’t finish. Try again.');
    if (q === 'app-created') setNote('The Hey Joe Sites GitHub App is set up. Connect GitHub to pick a repo.');
    if (q === 'app-failed') setErr('Setting up the GitHub App didn’t finish. Try again.');
    if (q) { const u = new URL(window.location.href); u.searchParams.delete('github'); window.history.replaceState(null, '', u.toString()); }
  }, []);

  if (!g) return null;
  // Not set up yet: only an admin sees this, once. It posts the app's settings to
  // GitHub's create-app page; one click there and GitHub hands the keys to Plus.
  if (!g.configured) {
    if (!g.canSetup) return null;
    const setUp = async () => {
      setBusy(true); setErr(null);
      try {
        const m = await fetchGithubAppManifest(site.id);
        const f = document.createElement('form'); f.method = 'POST'; f.action = m.action;
        const i = document.createElement('input'); i.type = 'hidden'; i.name = 'manifest'; i.value = m.manifest;
        f.appendChild(i); document.body.appendChild(f); f.submit();
      } catch (e) { setErr((e as Error).message); setBusy(false); }
    };
    return (
      <section className="flex flex-col" style={{ ...panel, gap: 12 }}>
        <h2 style={{ ...h2, display: 'flex', alignItems: 'center', gap: 8 }}><Github size={18} aria-hidden="true" />Deploy from GitHub</h2>
        <p style={small}>Only admins see this. Deploying from GitHub needs the Hey Joe Sites app on GitHub first. This opens GitHub with every setting filled in; click Create GitHub App there and you come back here.</p>
        <div><button type="button" onClick={setUp} disabled={busy} style={dark}>{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Github size={18} aria-hidden="true" />}Set up the GitHub App</button></div>
        {err && <p style={errText}>{err}</p>}
      </section>
    );
  }

  const run = async (fn: () => Promise<{ github: SiteGithub; site?: Site; queued?: boolean }>) => {
    setBusy(true); setErr(null); setNote(null);
    try {
      const r = await fn(); setG(r.github); if (r.site) onSite(r.site);
      if (r.queued) setNote('A deploy is already running. Your latest commit goes up right after it.');
      return r;
    } catch (e) { setErr((e as Error).message); return null; } finally { setBusy(false); }
  };
  const connect = async () => {
    setBusy(true); setErr(null);
    try { window.location.href = (await startGithubConnect(site.id)).url; } catch (e) { setErr((e as Error).message); setBusy(false); }
  };

  return (
    <section className="flex flex-col" style={{ ...panel, gap: 12 }}>
      <h2 style={{ ...h2, display: 'flex', alignItems: 'center', gap: 8 }}><Github size={18} aria-hidden="true" />Deploy from GitHub</h2>
      {!g.installed ? (
        <>
          <p style={small}>Connect a GitHub repo and every push becomes a new version of your site, so you don’t upload a zip each time.</p>
          <div><button type="button" onClick={connect} disabled={busy} style={dark}>{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Github size={18} aria-hidden="true" />}Connect GitHub</button></div>
        </>
      ) : editing || !g.repo ? (
        <RepoPicker site={site} g={g} busy={busy} onConnectMore={connect} onCancel={g.repo ? () => setEditing(false) : undefined}
          onSave={async (body) => { const r = await run(() => saveSiteGithub(site.id, body)); if (r) setEditing(false); }} />
      ) : (
        <>
          <p style={{ ...small, color: INK, wordBreak: 'break-word' }}>
            <strong>{g.repo.fullName}</strong>, branch <strong>{g.branch}</strong>{g.folder ? <>, folder <strong>{g.folder}</strong></> : null}
          </p>
          <label className="flex items-start" style={{ gap: 10, cursor: busy ? 'wait' : 'pointer' }}>
            <Checkbox checked={g.autoPublish} disabled={busy} style={{ marginTop: 2 }} onCheckedChange={(v) => run(() => setGithubAutoPublish(site.id, v === true))} />
            <span style={{ fontSize: 15, color: INK, lineHeight: 1.45 }}>Publish every push. When this is off, pushes go to your preview and you publish here.</span>
          </label>
          <LastDeploy g={g} />
          <div className="flex flex-wrap items-center" style={{ gap: 16 }}>
            <button type="button" onClick={() => run(() => deployFromGithub(site.id))} disabled={busy || g.deploying} style={{ ...dark, opacity: busy || g.deploying ? 0.6 : 1 }}>
              {busy || g.deploying ? <Loader2 className="w-4 h-4 animate-spin" /> : null}{busy || g.deploying ? 'Deploying' : 'Deploy now'}
            </button>
            <button type="button" onClick={() => setEditing(true)} disabled={busy} style={linkBtn}>Change repo</button>
            <button type="button" onClick={() => run(() => disconnectGithub(site.id))} disabled={busy} style={linkBtn}>Disconnect</button>
          </div>
        </>
      )}
      {note && <p style={small}>{note}</p>}
      {err && <p style={errText}>{err}</p>}
    </section>
  );
};

const LastDeploy: React.FC<{ g: SiteGithub }> = ({ g }) => {
  const l = g.last;
  if (g.deploying) return <p style={small}>Deploying your latest commit…</p>;
  if (!l) return null;
  const commit = l.sha ? `${l.sha.slice(0, 7)}${l.message ? ` “${l.message}”` : ''}` : '';
  if (!l.ok) return <p style={errText}>{when(l.at)}: {commit ? `${commit} didn’t deploy. ` : ''}{l.error}</p>;
  if (l.error) return <p style={errText}>{when(l.at)}: {commit} is on your preview, but it didn’t publish. {l.error}</p>;
  return <p style={small}>{when(l.at)}: {commit} {l.held ? 'is getting a quick review before it can go up.' : l.published ? 'is live.' : 'is on your preview, ready to publish.'}</p>;
};

const RepoPicker: React.FC<{ site: Site; g: SiteGithub; busy: boolean; onSave: (b: { repoId: number; branch: string; folder: string; autoPublish: boolean; rightsConfirmed: boolean }) => void; onCancel?: () => void; onConnectMore: () => void }> =
  ({ site, g, busy, onSave, onCancel, onConnectMore }) => {
  const [repos, setRepos] = useState<GithubRepo[] | null>(null);
  const [repoId, setRepoId] = useState<number | null>(g.repo?.id ?? null);
  const [branches, setBranches] = useState<string[]>([]);
  const [branch, setBranch] = useState(g.branch || '');
  const [folder, setFolder] = useState(g.folder || '');
  const [autoPublish, setAutoPublish] = useState(g.autoPublish);
  const [rights, setRights] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    fetchGithubRepos(site.id).then((r) => { setRepos(r.repos); if (!repoId && r.repos.length === 1) setRepoId(r.repos[0].id); }).catch((e) => setErr((e as Error).message));
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [site.id]);
  useEffect(() => {
    if (!repoId) return;
    fetchGithubBranches(site.id, repoId).then((r) => { setBranches(r.branches); setBranch((b) => (b && r.branches.includes(b) ? b : r.defaultBranch)); }).catch((e) => setErr((e as Error).message));
  }, [site.id, repoId]);

  if (!repos) return err ? <p style={errText}>{err}</p> : <p style={small}><Loader2 className="w-4 h-4 animate-spin inline" /> Loading your repos…</p>;
  const ready = !!repoId && !!branch && rights && !busy;
  return (
    <div className="flex flex-col" style={{ gap: 12 }}>
      {repos.length === 0 ? (
        <p style={small}>Hey Joe Sites can’t see any repos yet. <button type="button" onClick={onConnectMore} style={{ ...linkBtn, fontSize: 'inherit' }}>Pick repos on GitHub</button></p>
      ) : (
        <>
          <label className="flex flex-col" style={{ gap: 6 }}>
            <span style={label}>Repo</span>
            <select value={repoId ?? ''} onChange={(e) => { setRepoId(Number(e.target.value) || null); setBranch(''); }} style={input}>
              <option value="">Pick a repo</option>
              {repos.map((r) => <option key={r.id} value={r.id}>{r.fullName}{r.private ? ' (private)' : ''}</option>)}
            </select>
          </label>
          <p style={small}>Don’t see it? <button type="button" onClick={onConnectMore} style={{ ...linkBtn, fontSize: 'inherit' }}>Pick more repos on GitHub</button></p>
          <label className="flex flex-col" style={{ gap: 6 }}>
            <span style={label}>Branch</span>
            <select value={branch} disabled={!repoId} onChange={(e) => setBranch(e.target.value)} style={input}>
              {!branch && <option value="">Pick a branch</option>}
              {branches.map((b) => <option key={b} value={b}>{b}</option>)}
            </select>
          </label>
          <label className="flex flex-col" style={{ gap: 6 }}>
            <span style={label}>Folder with your index.html</span>
            <input value={folder} onChange={(e) => setFolder(e.target.value)} placeholder="Leave empty for the top of the repo" style={input} />
          </label>
          <label className="flex items-start" style={{ gap: 10, cursor: 'pointer' }}>
            <Checkbox checked={autoPublish} onCheckedChange={(v) => setAutoPublish(v === true)} style={{ marginTop: 2 }} />
            <span style={{ fontSize: 15, color: INK, lineHeight: 1.45 }}>Publish every push. When this is off, pushes go to your preview and you publish here.</span>
          </label>
          <label className="flex items-start" style={{ gap: 10, cursor: 'pointer' }}>
            <Checkbox checked={rights} onCheckedChange={(v) => setRights(v === true)} style={{ marginTop: 2 }} />
            <span style={{ fontSize: 15, color: INK, lineHeight: 1.45 }}>I have the rights to everything in this repo’s site.</span>
          </label>
          <div className="flex flex-wrap items-center" style={{ gap: 16 }}>
            <button type="button" disabled={!ready} onClick={() => repoId && onSave({ repoId, branch, folder: folder.trim(), autoPublish, rightsConfirmed: rights })}
              style={{ ...dark, opacity: ready ? 1 : 0.45, cursor: ready ? 'pointer' : 'not-allowed' }}>
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null}{busy ? 'Deploying' : 'Save and deploy'}
            </button>
            {onCancel && <button type="button" onClick={onCancel} disabled={busy} style={linkBtn}>Cancel</button>}
          </div>
        </>
      )}
      {err && <p style={errText}>{err}</p>}
    </div>
  );
};
